import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, TrendingDown, DollarSign, Users, Calendar, AlertTriangle, BarChart3, PieChart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayrollDashboardPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/payroll-analytics`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch payroll analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
    return amount.toFixed(0);
  };

  const formatMonth = (monthStr) => {
    const [y, m] = monthStr.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m) - 1]} ${y.slice(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="payroll-dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center py-8 text-muted-foreground" data-testid="payroll-dashboard-error">Unable to load payroll analytics.</div>;
  }

  const { monthly_trends, department_breakdown, lop_details, analysis_period } = analytics;

  // Calculate summary metrics from latest month with data
  const latestMonth = [...monthly_trends].reverse().find(m => m.employee_count > 0);
  const prevMonth = monthly_trends.length > 1 ? [...monthly_trends].reverse().find((m, i) => i > 0 && m.employee_count > 0) : null;

  const grossChange = latestMonth && prevMonth && prevMonth.total_gross > 0
    ? ((latestMonth.total_gross - prevMonth.total_gross) / prevMonth.total_gross * 100).toFixed(1)
    : null;

  // Find max value for chart scaling
  const maxGross = Math.max(...monthly_trends.map(m => m.total_gross), 1);
  const maxDeptCost = Math.max(...department_breakdown.map(d => d.total_cost), 1);

  return (
    <div className="space-y-6" data-testid="payroll-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="payroll-dashboard-title">Payroll Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Analysis period: {analysis_period}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500" data-testid="metric-total-gross">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Gross</p>
                <p className="text-2xl font-bold mt-1">
                  {latestMonth ? `₹${formatCurrency(latestMonth.total_gross)}` : '—'}
                </p>
                {grossChange && (
                  <div className="flex items-center gap-1 mt-1">
                    {parseFloat(grossChange) >= 0 ?
                      <TrendingUp className="h-3 w-3 text-emerald-500" /> :
                      <TrendingDown className="h-3 w-3 text-red-500" />}
                    <span className={`text-xs font-medium ${parseFloat(grossChange) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {grossChange}% MoM
                    </span>
                  </div>
                )}
              </div>
              <DollarSign className="h-8 w-8 text-blue-500 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500" data-testid="metric-total-net">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Net Payout</p>
                <p className="text-2xl font-bold mt-1">
                  {latestMonth ? `₹${formatCurrency(latestMonth.total_net)}` : '—'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {latestMonth ? `${latestMonth.employee_count} employees` : ''}
                </p>
              </div>
              <Users className="h-8 w-8 text-emerald-500 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500" data-testid="metric-total-deductions">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Deductions</p>
                <p className="text-2xl font-bold mt-1">
                  {latestMonth ? `₹${formatCurrency(latestMonth.total_deductions)}` : '—'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  TDS: ₹{latestMonth ? formatCurrency(latestMonth.total_tds) : '0'} | PF: ₹{latestMonth ? formatCurrency(latestMonth.total_pf) : '0'}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-amber-500 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${lop_details.length > 0 ? 'border-l-red-500' : 'border-l-slate-300'}`} data-testid="metric-lop">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">LOP Impact</p>
                <p className="text-2xl font-bold mt-1">
                  {latestMonth ? `₹${formatCurrency(latestMonth.total_lop_deduction)}` : '—'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {latestMonth?.total_lop_days || 0} days across {lop_details.length} employees
                </p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${lop_details.length > 0 ? 'text-red-500' : 'text-slate-400'} opacity-60`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Month-over-Month Trend */}
        <Card className="lg:col-span-2" data-testid="chart-monthly-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-500" />
              Monthly Payroll Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-end gap-2 h-48 mt-4">
              {monthly_trends.map((month, idx) => {
                const grossHeight = maxGross > 0 ? (month.total_gross / maxGross * 100) : 0;
                const netHeight = maxGross > 0 ? (month.total_net / maxGross * 100) : 0;
                return (
                  <div key={month.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`bar-${month.month}`}>
                    <div className="w-full flex gap-0.5 items-end" style={{ height: '160px' }}>
                      <div
                        className="flex-1 bg-blue-500/20 rounded-t transition-all duration-500 hover:bg-blue-500/30 relative group"
                        style={{ height: `${grossHeight}%`, minHeight: month.total_gross > 0 ? '4px' : '0' }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          Gross: ₹{formatCurrency(month.total_gross)}
                        </div>
                      </div>
                      <div
                        className="flex-1 bg-emerald-500 rounded-t transition-all duration-500 hover:bg-emerald-600 relative group"
                        style={{ height: `${netHeight}%`, minHeight: month.total_net > 0 ? '4px' : '0' }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          Net: ₹{formatCurrency(month.total_net)}
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground font-medium">{formatMonth(month.month)}</span>
                    <span className="text-[9px] text-muted-foreground">{month.employee_count}emp</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-blue-500/20 rounded" /> Gross</div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-emerald-500 rounded" /> Net Payout</div>
            </div>
          </CardContent>
        </Card>

        {/* Department Breakdown */}
        <Card data-testid="chart-department-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <PieChart className="h-4 w-4 text-violet-500" />
              Department Cost
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {department_breakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No department data yet</p>
            ) : (
              <div className="space-y-3 mt-4">
                {department_breakdown.map((dept, idx) => {
                  const pct = maxDeptCost > 0 ? (dept.total_cost / maxDeptCost * 100) : 0;
                  const colors = ['bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500'];
                  return (
                    <div key={dept.department} data-testid={`dept-${dept.department}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium truncate max-w-[120px]">{dept.department}</span>
                        <span className="text-xs text-muted-foreground">₹{formatCurrency(dept.total_cost)}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${colors[idx % colors.length]} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex justify-between mt-0.5">
                        <span className="text-[10px] text-muted-foreground">{dept.employee_count} employees</span>
                        <span className="text-[10px] text-muted-foreground">Avg: ₹{formatCurrency(dept.avg_salary)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* LOP Details */}
      {lop_details.length > 0 && (
        <Card data-testid="lop-details-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Loss of Pay Details — Current Month
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-4 font-medium text-muted-foreground">Employee</th>
                    <th className="py-2 pr-4 font-medium text-muted-foreground text-right">LOP Days</th>
                    <th className="py-2 font-medium text-muted-foreground text-right">Deduction</th>
                  </tr>
                </thead>
                <tbody>
                  {lop_details.map((emp, idx) => (
                    <tr key={idx} className="border-b last:border-0" data-testid={`lop-row-${idx}`}>
                      <td className="py-2 pr-4 font-medium">{emp.employee_name}</td>
                      <td className="py-2 pr-4 text-right">
                        <Badge variant="destructive" className="text-xs">{emp.lop_days} days</Badge>
                      </td>
                      <td className="py-2 text-right font-medium text-red-600">₹{emp.lop_deduction.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Detail Table */}
      <Card data-testid="monthly-detail-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            Month-wise Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4 font-medium text-muted-foreground">Month</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-center">Employees</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-right">Gross</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-right">Net</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-right">TDS</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-right">PF</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-right">LOP</th>
                </tr>
              </thead>
              <tbody>
                {[...monthly_trends].reverse().map((m) => (
                  <tr key={m.month} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`month-row-${m.month}`}>
                    <td className="py-2 pr-4 font-medium">{formatMonth(m.month)}</td>
                    <td className="py-2 pr-4 text-center">{m.employee_count}</td>
                    <td className="py-2 pr-4 text-right">₹{m.total_gross.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right text-emerald-600 font-medium">₹{m.total_net.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right">₹{m.total_tds.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right">₹{m.total_pf.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right">
                      {m.total_lop_deduction > 0 ? (
                        <span className="text-red-600">₹{m.total_lop_deduction.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollDashboardPage;
